/** Customer and machine forms, used inline on New Work Order and on the record pages. */
import { useState } from 'react';
import { api, ApiError, errorText } from '../lib/api';
import { useSettings } from '../lib/hooks';
import type { CustomerRow, MachineRow } from '../lib/types';
import { Button, ErrorText, Field, Input, Select, Textarea } from './ui';

export function CustomerForm({
  initial,
  onSaved,
  onCancel,
}: {
  initial?: CustomerRow;
  onSaved: (c: CustomerRow) => void;
  onCancel?: () => void;
}) {
  const [f, setF] = useState({
    name: initial?.name ?? '',
    phone: initial?.phone ?? '',
    email: initial?.email ?? '',
    billingAddress: initial?.billingAddress ?? '',
    notes: initial?.notes ?? '',
  });
  const [error, setError] = useState<string | null>(null);
  const [dupes, setDupes] = useState<CustomerRow[] | null>(null);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });

  const save = async (confirmDuplicate = false) => {
    setError(null);
    try {
      const c = initial
        ? await api.patch<CustomerRow>(`/api/customers/${initial.id}`, f)
        : await api.post<CustomerRow>('/api/customers', { ...f, confirmDuplicate });
      onSaved(c);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409 && !initial) {
        setDupes((e.details as { duplicates: CustomerRow[] }).duplicates);
      } else setError(errorText(e));
    }
  };

  return (
    <div className="space-y-3">
      <ErrorText error={error} />
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Name" required><Input value={f.name} onChange={set('name')} autoFocus /></Field>
        <Field label="Phone"><Input value={f.phone} onChange={set('phone')} /></Field>
        <Field label="Email"><Input type="email" value={f.email} onChange={set('email')} /></Field>
        <Field label="Billing address"><Input value={f.billingAddress} onChange={set('billingAddress')} /></Field>
      </div>
      <Field label="Notes"><Textarea rows={2} value={f.notes} onChange={set('notes')} /></Field>
      {dupes && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm">
          <p className="font-semibold">This looks like an existing customer:</p>
          <ul className="my-2 list-disc pl-5">
            {dupes.map((d) => (
              <li key={d.id}>
                {d.name} {d.phone && `· ${d.phone}`}{' '}
                <button className="text-blue-700 underline" onClick={() => onSaved(d)}>Use this one</button>
              </li>
            ))}
          </ul>
          <Button variant="secondary" onClick={() => save(true)}>Create a new one anyway</Button>
        </div>
      )}
      <div className="flex justify-end gap-2">
        {onCancel && <Button variant="secondary" onClick={onCancel}>Cancel</Button>}
        <Button onClick={() => save()} disabled={!f.name.trim()}>{initial ? 'Save' : 'Add customer'}</Button>
      </div>
    </div>
  );
}

export function MachineForm({
  initial,
  customerId,
  onSaved,
  onCancel,
}: {
  initial?: MachineRow;
  customerId?: number | null;
  onSaved: (m: MachineRow) => void;
  onCancel?: () => void;
}) {
  const settings = useSettings();
  const [f, setF] = useState({
    make: initial?.make ?? '',
    model: initial?.model ?? '',
    serialNumber: initial?.serialNumber ?? '',
    equipmentNo: initial?.equipmentNo ?? '',
    controls: initial?.controls ?? '',
    isRentalFleet: initial?.isRentalFleet ?? false,
  });
  const [error, setError] = useState<string | null>(null);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });

  const save = async () => {
    setError(null);
    try {
      const body = { ...f, controls: f.controls || null, ...(initial ? {} : { customerId: customerId ?? null }) };
      const m = initial ? await api.patch<MachineRow>(`/api/machines/${initial.id}`, body) : await api.post<MachineRow>('/api/machines', body);
      onSaved(m);
    } catch (e) {
      setError(errorText(e));
    }
  };

  return (
    <div className="space-y-3">
      <ErrorText error={error} />
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Make" required><Input value={f.make} onChange={set('make')} autoFocus placeholder="CAT, Deere, Kubota…" /></Field>
        <Field label="Model (M/N)" required><Input value={f.model} onChange={set('model')} /></Field>
        <Field label="Serial number (S/N)" required><Input value={f.serialNumber} onChange={set('serialNumber')} /></Field>
        <Field label="Equipment # (EQ #)"><Input value={f.equipmentNo} onChange={set('equipmentNo')} /></Field>
        <Field label="Controls">
          <Select value={f.controls} onChange={set('controls')}>
            <option value="">—</option>
            {settings.data?.controls.map((c) => <option key={c}>{c}</option>)}
          </Select>
        </Field>
        <label className="flex items-center gap-2 self-end pb-2 text-sm">
          <input type="checkbox" checked={f.isRentalFleet} onChange={(e) => setF({ ...f, isRentalFleet: e.target.checked })} />
          Our rental fleet
        </label>
      </div>
      <div className="flex justify-end gap-2">
        {onCancel && <Button variant="secondary" onClick={onCancel}>Cancel</Button>}
        <Button onClick={save} disabled={!f.make.trim() || !f.model.trim() || !f.serialNumber.trim()}>{initial ? 'Save' : 'Add machine'}</Button>
      </div>
    </div>
  );
}
